import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, shallowEqual, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Tooltip as ReactTooltip } from 'react-tooltip';

/* components */
import Form, { ValidationMode } from 'components/Common/Form';
import FormControl from 'components/Common/FormControl';
import Toast, { Type as ToastType } from 'components/Common/Toast';
import Input, {
  Type as InputType,
  Size as InputSize,
  Color as InputColor,
} from 'components/Common/Input';
import LayoutGap from 'components/Common/LayoutGap';
import PageHeader from 'components/Common/PageHeader';
import Fieldset from 'components/Common/Fieldset';
import Button from 'components/Common/Button';
import Checkbox, { Size as CheckboxSize } from 'components/Common/Checkbox';
import FormControlSelect, {
  Color as SelectColor,
} from 'components/Common/FormControlSelect';
import FormControlDatePicker from 'components/Common/FormControlDatePicker';
import Textarea from 'components/Common/Textarea';
import MerchantLocations from 'refactored/components/Common/MerchantLocations';
import AdminCommissionSplit from 'components/Forms/AdminCommissionSplit';

/* redux */
import { clearOffersErrors, editOffer } from 'state/actions/offers';
import { selectEditOfferState } from 'state/selectors/offers';
import { fetchMerchantIssuers } from 'state/actions/merchants';
import { selectFetchMerchantNetworksState } from 'state/selectors/merchantNetworks';
import { fetchMerchantNetworks } from 'state/actions/merchantNetworks';
import selectFeatureFlagsState from 'state/selectors/featureFlags';

/* enums */
import StatusValue from 'enums/status/statusValue.enum';
import { CommissionType } from 'enums/offers/commissionTypes.enum';
import Path from 'enums/path.enum';

/* assets */
import dollarIcon from 'assets/icons/dollar.png';
import percentIcon from 'assets/icons/percent.png';

/* utils */
import {
  defaultOfferStatusOptions,
  closedStatusOptions,
} from 'utils/common/values';
import {
  MerchantCategoryCodeOptions,
  filterMerchantCategoryCode,
} from 'utils/offers/merchantCategoryCodes';
import { commissionTypes, offerTypes, sources } from 'utils/offers/values';
import { useSegments } from '@/refactored/hooks/useSegment';
import {
  shiftUTCDateToAppearLikeLocalTimeZoneInDatePicker,
  shiftDateFromDatePickerBackToUTC,
} from './utils';

import CommissionValueField from './CommissionValueField';
import validationSchema from './OfferEdit.schema';
import classes from './OfferEdit.module.scss';
import QualifiedIssuersField from './QualifiedIssuersField';
import AttributionField from './AttributionField';

const SELECT_ALL_WHITESPACE_REGEX = /\s/g;

const CommissionTypeIcon = Object.freeze({
  [CommissionType.Percent]: percentIcon,
  [CommissionType.Flat]: dollarIcon,
});

const OfferEdit = ({ offer }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { merchantNetworks } = useSelector(
    selectFetchMerchantNetworksState,
    shallowEqual,
  );

  useEffect(() => {
    dispatch(fetchMerchantNetworks());
  }, [dispatch]);

  const merchantNetworkOptions = merchantNetworks.map((item) => ({
    label: item?.name,
    value: item?.name,
  }));

  const [isEditing, setIsEditing] = useState(false);

  const [currentValue, setCurrentValue] = useState(offer);

  const [shouldResetForm, setShouldResetForm] = useState(false);

  const [
    {
      data: segmentsData,
      loading: loadingFetchSegments,
      error: errorFetchSegments,
    },
  ] = useSegments({
    limit: 1000,
    includeRules: false,
  });

  const segmentResults = useMemo(
    () => segmentsData?.results || [],
    [segmentsData],
  );

  const { loading: loadingEditOffer, success: editOfferSuccess } = useSelector(
    selectEditOfferState,
    shallowEqual,
  );

  // setting isEditing in useEffect to avoid state updates during Toast render
  useEffect(() => {
    if (editOfferSuccess) {
      setIsEditing(false);
      dispatch(clearOffersErrors());
    }
  }, [dispatch, editOfferSuccess]);

  useEffect(() => {
    // Reset shouldResetForm back to false, since shouldReset has already reset form to defaultValues
    if (shouldResetForm) {
      setShouldResetForm(false);
    }
  }, [setShouldResetForm, shouldResetForm]);

  const [locationSpecific, setLocationSpecific] = useState(
    currentValue.isLocationSpecific,
  );
  const [optInRequired, setOptInRequired] = useState(
    currentValue.optInRequired,
  );
  const [redeemableOnce, setRedeemableOnce] = useState(
    currentValue.redeemableOnce,
  );
  const [displayAlways, setDisplayAlways] = useState(
    currentValue.displayAlways ?? false,
  );
  const [selectedCommissionType, setSelectedCommissionType] = useState(
    offer?.commissionType,
  );

  const locationMissingError = !currentValue.merchantLocationIds?.length;

  // start and expiration date are stored "2021-07-06T20:16:29.693+00:00" as UTC Time
  // we want to display as Date object "Jul 06 2021 20:16:29 " without converting to local time.
  const [expDate, setExpDate] = useState(
    shiftUTCDateToAppearLikeLocalTimeZoneInDatePicker(
      currentValue.expirationDate,
    ),
  );
  const [startDateUTC, setStartDateUTC] = useState(
    shiftUTCDateToAppearLikeLocalTimeZoneInDatePicker(currentValue.startDate),
  );

  const formRef = useRef('');

  const { flags } = useSelector(selectFeatureFlagsState, shallowEqual);

  const { REWARDS_2653_UPCOMING_OFFERS_FLAG, IS_1917_REWARDS_MAINTANENCE } =
    flags;

  const segmentsOptions = useMemo(
    () =>
      segmentResults?.map((segment) => ({
        label: segment.name,
        value: segment.id,
      })),
    [segmentResults],
  );

  useEffect(() => {
    dispatch(fetchMerchantIssuers());
  }, [dispatch, offer]);

  const editableTextColor = !isEditing ? InputColor.Gray : InputColor.Black;
  const editableSelectColor = !isEditing ? SelectColor.Gray : SelectColor.Black;

  const onEditHandler = useCallback(() => setIsEditing(true), []);

  const onCancelHandler = useCallback(() => {
    setIsEditing(false);
    setShouldResetForm(true);

    // checkboxes to reset manually
    setLocationSpecific(currentValue.isLocationSpecific);
    setOptInRequired(currentValue.optInRequired);
    setRedeemableOnce(currentValue.redeemableOnce);
    setDisplayAlways(currentValue.displayAlways ?? false);
  }, [currentValue]);

  const onChangeCheckboxStateHandler = useCallback((setState) => {
    setState((prevState) => !prevState);
  }, []);

  const onChangeStatusHandler = useCallback(
    (newStatus) => {
      // If changing status from Active -> Inactive, set the expDate to today
      setExpDate(
        shiftUTCDateToAppearLikeLocalTimeZoneInDatePicker(
          newStatus === StatusValue.Inactive
            ? new Date()
            : currentValue.expirationDate,
        ),
      );
    },
    [currentValue.expirationDate],
  );

  const isValidNumber = useCallback(
    (value) => value !== undefined && value >= 0,
    [],
  );

  const onCloneHandler = useCallback(() => {
    navigate(`${Path.Offers}/clone/${offer._id}`, { state: { currentValue } });
  }, [navigate, offer, currentValue]);

  // Format as Date Object on Edit Mode
  const formatStartDate = currentValue.startDate ? startDateUTC : undefined;

  const formatExpirationDate = currentValue.expirationDate
    ? expDate
    : undefined;

  const onSubmitHandler = useCallback(
    (values) => {
      let segments = [];
      if (Array.isArray(values.segments)) segments = values.segments;
      if (typeof values.segments === 'string' && values.segments.length > 0)
        segments = [values.segments];

      const body = {
        ...values,

        status: values.status,
        isLocationSpecific: locationSpecific,
        optInRequired,
        startDate: shiftDateFromDatePickerBackToUTC(values.startDate), // Format as UTC String
        expirationDate: shiftDateFromDatePickerBackToUTC(values.expirationDate), // Format as UTC String
        merchantId:
          values.merchantId.replace(SELECT_ALL_WHITESPACE_REGEX, '') ?? '',
        redeemableOnce,
        commissionValue: Number(values.commissionValue),
        minTransactionAmount: Number(values.minTransactionAmount),
        maxTransactionAmount: Number(values.maxTransactionAmount),
        minRewardAmount: Number(values.minRewardAmount),
        maxRewardAmount: Number(values.maxRewardAmount),
        minUserRewardAmount: Number(values.minUserRewardAmount),
        maxUserRewardAmount: Number(values.maxUserRewardAmount),
        mcc: values.mcc ?? [],
        displayAlways,
        merchantLocationIds: offer.merchantLocationIds,
        segments,
        attribution: {
          isRequired: values.attribution.isRequired,
          // IMPRESSION is the only event currently used for attribution settings so we hardcode it.
          events: values.attribution.isRequired ? ['IMPRESSION'] : [],
        },
      };

      setCurrentValue({
        ...body,
        issuerCommissionsMap: currentValue.issuerCommissionsMap,
        userCommissionsMap: currentValue.userCommissionsMap,
      });

      // Remove any non-qualified issuers from commission maps
      const issuerCommissionsMap = {};
      const userCommissionsMap = {};

      body.qualifiedIssuer.forEach((issuerName) => {
        issuerCommissionsMap[issuerName] =
          body.issuerCommissionsMap[issuerName];
        userCommissionsMap[issuerName] = body.userCommissionsMap[issuerName];
      });

      body.issuerCommissionsMap = issuerCommissionsMap;
      body.userCommissionsMap = userCommissionsMap;

      delete body.category;
      delete body.merchantName;
      delete body.offerId;
      delete body._id;
      delete body.createdDate;
      delete body.lastModified;
      delete body.merchant;
      delete body.isTargeted;

      delete body.merchantLocationIds;

      if (!values.terms) {
        delete body.terms;
      }

      if (!values.partnerOfferId) {
        delete body.partnerOfferId;
      }

      if (!isValidNumber(values.minTransactionAmount)) {
        delete body.minTransactionAmount;
      }

      if (!isValidNumber(values.maxTransactionAmount)) {
        delete body.maxTransactionAmount;
      }

      if (!isValidNumber(values.minRewardAmount)) {
        delete body.minRewardAmount;
      }

      if (!isValidNumber(values.maxRewardAmount)) {
        delete body.maxRewardAmount;
      }

      if (!isValidNumber(values.minUserRewardAmount)) {
        delete body.minUserRewardAmount;
      }

      if (!isValidNumber(values.maxUserRewardAmount)) {
        delete body.maxUserRewardAmount;
      }

      if (!values.startDate) {
        delete body.startDate;
      }

      dispatch(editOffer(offer._id, body));
    },
    [
      offer,
      locationSpecific,
      optInRequired,
      redeemableOnce,
      dispatch,
      isValidNumber,
      displayAlways,
      currentValue.issuerCommissionsMap,
      currentValue.userCommissionsMap,
    ],
  );

  const commissionTypeIcon = CommissionTypeIcon[selectedCommissionType];

  let statusOptions =
    currentValue.status === StatusValue.Closed
      ? closedStatusOptions
      : defaultOfferStatusOptions;

  if (locationMissingError) {
    statusOptions = statusOptions.map((option) => {
      if (option.value === StatusValue.Active) {
        return {
          ...option,
          isDisabled: true,
        };
      }
      return option;
    });
  }

  const defaultStatusOptionsWithoutUpcoming = defaultOfferStatusOptions.filter(
    (option) => option.value !== StatusValue.Upcoming,
  );

  const isClosed = currentValue.status === StatusValue.Closed;

  return (
    <>
      {IS_1917_REWARDS_MAINTANENCE && (
        <Toast
          id="rewards-maintainence-flag"
          text="REWARDS MAINTANENCE ONGOING. RESTRICTING ACCESS TO OFFER EDIT/CREATE FUNCTIONALITY"
          type={ToastType.Error}
        />
      )}

      <Form
        onSubmit={onSubmitHandler}
        validationSchema={validationSchema}
        validationMode={ValidationMode.OnChange}
        className="ml-10 mt-10"
        ref={formRef}
        shouldReset={shouldResetForm}
        defaultValues={currentValue}
      >
        <PageHeader
          title={isEditing ? 'Edit Offer' : 'Offer Information'}
          className="mr-[2.5rem]"
        >
          {!isEditing ? (
            <>
              <span data-tooltip-id="edit-offer-disabled">
                <Button
                  key="edit"
                  onClick={onEditHandler}
                  disabled={isClosed}
                  className={classes.primaryButton}
                >
                  Edit offer
                </Button>
              </span>
              <ReactTooltip
                id="edit-offer-disabled"
                offset={0}
                hidden={!isClosed}
                place="bottom"
                content="Cannot edit a closed offer"
              />
              <Button variant="secondary" key="clone" onClick={onCloneHandler}>
                Clone
              </Button>
            </>
          ) : (
            <>
              <Button
                key="save-create"
                type="submit"
                loading={loadingEditOffer}
                disabled={IS_1917_REWARDS_MAINTANENCE}
              >
                {isEditing ? 'Save changes' : 'Edit'}
              </Button>
              <Button
                variant="secondary"
                key="go-back-cancel"
                onClick={onCancelHandler}
              >
                Cancel
              </Button>
            </>
          )}
        </PageHeader>
        <div>
          <Fieldset title="Offer Details">
            <LayoutGap direction="column" size="none">
              <FormControl
                name="offerId"
                render={(props) => (
                  <Input
                    label="Offer ID:"
                    labelClassName={classes.labelWidth}
                    value={offer._id}
                    type={InputType.Text}
                    readOnly
                    size={InputSize.S}
                    disabled
                    {...props}
                  />
                )}
              />
              <FormControl
                name="merchantId"
                render={(props) => (
                  <Input
                    label="Merchant ID:"
                    labelClassName={classes.labelWidth}
                    value={currentValue.merchantId}
                    type={InputType.Text}
                    readOnly
                    size={InputSize.S}
                    disabled
                    {...props}
                  />
                )}
              />
              <>
                <FormControl
                  name="merchantName"
                  render={(props) => (
                    <Input
                      label="Merchant Name:"
                      labelClassName={classes.labelWidth}
                      value={currentValue.merchantName}
                      type={InputType.Text}
                      readOnly
                      size={InputSize.S}
                      disabled
                      {...props}
                    />
                  )}
                />
              </>
              <FormControl
                name="name"
                render={(props) => (
                  <Input
                    label="Offer Name:"
                    labelClassName={classes.labelWidth}
                    value={offer.name}
                    type={InputType.Text}
                    readOnly={!isEditing}
                    disabled={!isEditing}
                    size={InputSize.S}
                    color={editableTextColor}
                    {...props}
                  />
                )}
              />
              <FormControlDatePicker
                name="startDate"
                label="Start Date:"
                labelClassName={classes.labelWidth}
                defaultValue={formatStartDate}
                readOnly={!isEditing}
                color={editableSelectColor}
                disabled={!isEditing}
                showTimeSelect
                onChangeManual={setStartDateUTC}
                isUTC
                testId="start-date-picker"
              />

              <FormControlDatePicker
                name="expirationDate"
                label="Expiration Date:"
                labelClassName={classes.labelWidth}
                defaultValue={formatExpirationDate}
                readOnly={!isEditing}
                disabled={!isEditing}
                color={editableSelectColor}
                showTimeSelect
                onChangeManual={setExpDate}
                isUTC
                testId="expiration-date-picker"
              />
              {REWARDS_2653_UPCOMING_OFFERS_FLAG ? (
                <FormControlSelect
                  name="status"
                  defaultValue={currentValue.status}
                  label="Status"
                  labelClassName={classes.labelWidth}
                  readOnly={
                    !isEditing || currentValue.status === StatusValue.Closed
                  }
                  disabled={!isEditing}
                  options={statusOptions}
                  color={editableSelectColor}
                  onChangeManual={onChangeStatusHandler}
                />
              ) : (
                <FormControlSelect
                  name="status"
                  defaultValue={currentValue.status}
                  label="Status"
                  labelClassName={classes.labelWidth}
                  readOnly={
                    !isEditing ||
                    currentValue.status === StatusValue.Closed ||
                    locationMissingError
                  }
                  disabled={!isEditing}
                  options={
                    currentValue.status === StatusValue.Closed
                      ? closedStatusOptions
                      : defaultStatusOptionsWithoutUpcoming
                  }
                  color={editableSelectColor}
                  onChangeManual={onChangeStatusHandler}
                  tooltip={
                    locationMissingError && isEditing
                      ? 'Please associate locations with this offer before activating it.'
                      : ''
                  }
                />
              )}
              <FormControlSelect
                name="source"
                defaultValue={currentValue.source}
                label="Source:"
                labelClassName={classes.labelWidth}
                readOnly={!isEditing}
                disabled={!isEditing}
                options={sources}
                color={editableSelectColor}
                testId="source-input"
              />
              <FormControlSelect
                name="offerType"
                defaultValue={currentValue.offerType}
                label="Offer Type:"
                labelClassName={classes.labelWidth}
                readOnly={!isEditing}
                disabled={!isEditing}
                options={offerTypes}
                color={editableSelectColor}
                testId="offer-type-input"
              />
              <FormControlSelect
                name="merchantNetwork"
                label="Merchant Network:"
                labelClassName={classes.labelWidth}
                defaultValue={currentValue.merchantNetwork}
                readOnly={!isEditing}
                disabled={!isEditing}
                options={merchantNetworkOptions}
                color={editableSelectColor}
                testId="merchant-network-input"
              />
              <FormControl
                name="partnerOfferId"
                render={(props) => (
                  <Input
                    label="Partner Offer ID:"
                    labelClassName={classes.labelWidth}
                    value={offer.partnerOfferId}
                    type={InputType.Text}
                    readOnly={!isEditing}
                    disabled={!isEditing}
                    size={InputSize.S}
                    color={editableTextColor}
                    {...props}
                  />
                )}
              />
              <Checkbox
                label="Location Specific"
                setSelected={() =>
                  onChangeCheckboxStateHandler(setLocationSpecific)
                }
                selected={locationSpecific}
                className={classes.checkbox}
                size={CheckboxSize.S}
                disabled={!isEditing}
              />
              <FormControlSelect
                name="segments"
                label="Segment:"
                defaultValue={currentValue?.segments?.[0]}
                labelClassName={classes.labelWidth}
                readOnly={!isEditing}
                options={segmentsOptions || []}
                color={editableSelectColor}
                isClearable
                isLoading={loadingFetchSegments}
                disabled={loadingFetchSegments || !isEditing}
              />
              {segmentsOptions.length === 0 && errorFetchSegments && (
                <p className="text-red-500">
                  Error loading segments. Try refreshing the page. If the issue
                  persists, please reach out to the Back Office team.
                </p>
              )}
              <FormControlSelect
                filterOption={filterMerchantCategoryCode}
                name="mcc"
                defaultValue={currentValue.mcc}
                label="Merchant Category Codes:"
                labelClassName={classes.labelWidth}
                readOnly={!isEditing}
                disabled={!isEditing}
                options={MerchantCategoryCodeOptions}
                isMulti
                allowSelectAll={false}
                noOptionsMessage={() =>
                  'Start typing MCC number or keyword for suggestions...'
                }
                color={editableSelectColor}
              />
              <FormControl
                name="terms"
                render={(props) => (
                  <Textarea
                    label="Terms And Conditions:"
                    labelClassName={classes.labelWidth}
                    value={currentValue.terms}
                    preventNewLines
                    readOnly={!isEditing}
                    size={InputSize.S}
                    color={editableTextColor}
                    {...props}
                  />
                )}
              />
            </LayoutGap>
          </Fieldset>
          <Fieldset title="Commission Details">
            <QualifiedIssuersField
              currentValue={currentValue}
              isEditing={isEditing}
              editableSelectColor={editableSelectColor}
            />
            <LayoutGap>
              <FormControlSelect
                name="commissionType"
                defaultValue={currentValue.commissionType}
                label="Commission Type"
                labelClassName={classes.labelWidth}
                readOnly={!isEditing}
                disabled={!isEditing}
                options={commissionTypes}
                color={editableSelectColor}
                onChangeManual={setSelectedCommissionType}
                testId="commission-type-input"
                className="mb-[0] w-[15rem]"
              />
              <CommissionValueField
                currentValue={currentValue}
                isEditing={isEditing}
                commissionTypeIcon={commissionTypeIcon}
                editableTextColor={editableTextColor}
              />
            </LayoutGap>
            <AdminCommissionSplit
              commissionType={selectedCommissionType}
              isEditing={isEditing}
            />
          </Fieldset>
          <Fieldset title="Redemption Details">
            <LayoutGap>
              <FormControl
                name="minTransactionAmount"
                className="mb-[0]"
                render={(props) => (
                  <Input
                    label="Min Txn Amount In Cents"
                    labelClassName={classes.labelWidth}
                    value={currentValue.minTransactionAmount}
                    type={InputType.Number}
                    readOnly={!isEditing}
                    color={editableTextColor}
                    width="w-[15rem]"
                    {...props}
                  />
                )}
              />
              <FormControl
                name="maxTransactionAmount"
                className="mb-[0]"
                render={(props) => (
                  <Input
                    label="Max Txn Amount In Cents"
                    labelClassName={classes.labelWidth}
                    value={currentValue.maxTransactionAmount}
                    type={InputType.Number}
                    readOnly={!isEditing}
                    color={editableTextColor}
                    width="w-[15rem]"
                    {...props}
                  />
                )}
              />
            </LayoutGap>
            <LayoutGap>
              <FormControl
                name="minRewardAmount"
                className="mb-[0]"
                render={(props) => (
                  <Input
                    label="Min Reward Amount In Cents"
                    labelClassName={classes.labelWidth}
                    value={currentValue.minRewardAmount}
                    type={InputType.Number}
                    readOnly={!isEditing}
                    color={editableTextColor}
                    width="w-[15rem]"
                    {...props}
                  />
                )}
              />
              <FormControl
                name="maxRewardAmount"
                className="mb-[0]"
                render={(props) => (
                  <Input
                    label="Max Reward Amount In Cents"
                    labelClassName={classes.labelWidth}
                    value={currentValue.maxRewardAmount}
                    type={InputType.Number}
                    readOnly={!isEditing}
                    color={editableTextColor}
                    width="w-[15rem]"
                    {...props}
                  />
                )}
              />
            </LayoutGap>
            <LayoutGap>
              <FormControl
                name="minUserRewardAmount"
                className="mb-[0]"
                render={(props) => (
                  <Input
                    label="Min User Reward Amount In Cents"
                    labelClassName={classes.labelWidth}
                    value={currentValue.minUserRewardAmount}
                    type={InputType.Number}
                    readOnly={!isEditing}
                    color={editableTextColor}
                    width="w-[15rem]"
                    {...props}
                  />
                )}
              />
              <FormControl
                name="maxUserRewardAmount"
                className="mb-[0]"
                render={(props) => (
                  <Input
                    label="Max User Reward Amount In Cents"
                    labelClassName={classes.labelWidth}
                    value={currentValue.maxUserRewardAmount}
                    type={InputType.Number}
                    readOnly={!isEditing}
                    color={editableTextColor}
                    width="w-[15rem]"
                    {...props}
                  />
                )}
              />
            </LayoutGap>
            <Checkbox
              label="Opt-in Required"
              setSelected={() => onChangeCheckboxStateHandler(setOptInRequired)}
              selected={optInRequired}
              className={classNames(classes.checkbox, 'mb-[0]')}
              disabled={!isEditing}
              size={CheckboxSize.S}
            />
            <Checkbox
              label="Redeemable Once"
              setSelected={() =>
                onChangeCheckboxStateHandler(setRedeemableOnce)
              }
              selected={redeemableOnce}
              className={classNames(classes.checkbox, 'mb-[0]')}
              disabled={!isEditing}
              size={CheckboxSize.S}
            />
            <Checkbox
              label="Always Appears to Cardholder"
              setSelected={() => onChangeCheckboxStateHandler(setDisplayAlways)}
              selected={displayAlways}
              className={classNames(classes.checkbox, 'mb-[0]')}
              disabled={!isEditing}
              size={CheckboxSize.S}
            />
            <AttributionField isEditing={isEditing} />
          </Fieldset>
        </div>
      </Form>
      <MerchantLocations
        merchantLocationIds={currentValue.merchantLocationIds}
      />
    </>
  );
};

OfferEdit.propTypes = {
  offer: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.any])).isRequired,
};

export default OfferEdit;
